/* eslint-disable @typescript-eslint/ban-types */
// importer la librairie de react
import React, { useEffect, useRef, useState } from "react"
import {
    Box,
    Icon,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import PasswordIcon from "@mui/icons-material/Password"
import { inputLabelClasses } from "@mui/material/InputLabel"
import { useTranslation } from "react-i18next"
import PhoneInput, { CountryData } from "react-phone-input-2"
import fr from "date-fns/locale/fr"
import Utils from "../../utils/stringUtils"
import { PageConfig, CwComponentConfig } from "../../models/pageConfig"
import UIService from "../../services/UIService"
import ActionService from "../../services/action"
import DialogService from "../../services/dialog"
import { DialogType } from "../../models/dialog"
import StoreService from "../../services/store"
import FormFieldService from "../../services/formField"
import DynamicParamsService from "../../services/dynamicParams"
import "react-phone-input-2/lib/style.css"

const CwTextfield = (
    props: PageConfig<CwComponentConfig.TextField>
): React.JSX.Element | null => {
    const { config } = props
    // création de toute les variables à partir des config
    const {
        margin,
        variant,
        fieldName,
        fieldType,
        multiline,
        edit,
        variantTypo,
        labelIcon,
        pipe,
        controlled,
        useRefreshListener,
        useConfigListener,
        error,
        max,
        formLink,
        borderRadius,
        required,
        onChangeActions,
        minRows,
        maxRows,
        labelClassName,
    } = config

    const [background, setBackground] = useState(
        config?.background
            ? UIService.getLinearGradByName(config.background)
            : ""
    )
    const [loading, setLoading] = useState(true)
    const [value, setValue] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [errorState, setError] = useState<{
        hasError: boolean
        helperText?: string
    }>({ hasError: false, helperText: "" })
    const className = config.className || ""
    const textFieldClass = config.textFieldClass || ""
    const readOnly = config.readOnly ? config.readOnly : false
    const lang = useRef<string>("fr")
    const [hidden, setHidden] = useState<boolean>(config.hidden || false)
    const label = DynamicParamsService.resolveLang(config.label)
    const { t, i18n } = useTranslation()
    const phoneCountryFromLang = ((): string => {
        const l = (i18n.language || "fr").split("-")[0].toLowerCase()
        if (l === "en") return "gb"
        if (l === "es" || l === "it" || l === "fr") return l
        return "fr"
    })()
    /**
     * Resolve la valeur à afficher
     * @param val new value
     */
    const resolveValue = async (val: any) => {
        const res = await DynamicParamsService.resolve([val])
        if ((res[0] || res[0] === 0 || res[0] === "") && fieldName) {
            const resolvedValue = res[0] as string
            let valueFromConfig
            if (fieldType === "currency")
                valueFromConfig = Utils.specialTypeRender(
                    "currency",
                    resolvedValue,
                    fieldName
                )
            // formattage  de field phone
            else if (fieldType === "phone") {
                const raw = (resolvedValue || "") as string
                const currentLang = (lang.current || "fr").toLowerCase()
                const defaultDialByLang: Record<string, string> = {
                    fr: "33",
                    it: "39",
                    en: "44",
                    gb: "44",
                    es: "34",
                }
                const defaultDial = defaultDialByLang[currentLang] || "33"

                // Normalize to digits only for react-phone-input-2 controlled value
                let numeric = raw.replace(/\D/g, "")

                // French-specific normalization of local format starting with 0
                if (currentLang === "fr") {
                    if (raw.charAt(0) === "0") {
                        numeric = defaultDial + raw.slice(1).replace(/\D/g, "")
                    } else if (
                        raw &&
                        raw.charAt(0) !== "+" &&
                        raw.charAt(0) !== "0"
                    ) {
                        numeric = defaultDial + numeric
                    }
                }

                // If empty, seed with country dial code so typing isn't blocked
                if (!numeric) numeric = defaultDial

                valueFromConfig = numeric
            } else valueFromConfig = resolvedValue
            setValue(valueFromConfig as string)
            StoreService.getInstance().local.set(fieldName, res[0])
        }
    }

    const init = async () => {
        const l = await StoreService.getInstance().persistant.get("lang")
        lang.current = l || "fr"
        // resolve value from config
        if (config.value || controlled) {
            await resolveValue(config.value)
        }
        // exec les action d'init si présentes
        if (props.actions && props.actions.length)
            ActionService.exec(props.actions).then((conf) => {
                // override des attrs de state par ceux de conf
                if (conf?.type === "config") {
                    //bg
                    if (conf.value?.background)
                        setBackground(
                            conf.value.background
                                ? UIService.getLinearGradByName(
                                    conf.value.background
                                )
                                : ""
                        )
                    // value
                    if (conf.value?.value) resolveValue(conf.value.value)

                    // affichage
                    if (typeof conf.value?.hidden === "boolean")
                        setHidden(conf.value.hidden)
                }
            })

        setLoading(false)
    }

    useEffect(() => {
        let subRefreshId: string | null = null
        let subConfigId: string | null = null
        // on s'abonne aux changements de route
        if (useRefreshListener) {
            subRefreshId = DialogService.getInstance().subscribe(
                "pageRefesh",
                DialogType.REFRESH,
                props.name,
                () => {
                    init()
                }
            )
        }

        // abonnement aux changments de config (hide / show)
        if (useConfigListener)
            subConfigId = DialogService.getInstance().subscribe(
                "all",
                DialogType.CONFIG,
                props.name,
                (param: any) => {
                    if (typeof param.hidden === "boolean")
                        setHidden(param.hidden)
                }
            )

        init()
        // unsubscribe
        return () => {
            if (subRefreshId)
                DialogService.getInstance().unsubscribe(subRefreshId)
            if (subConfigId)
                DialogService.getInstance().unsubscribe(subConfigId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (controlled)
        useEffect(() => {
            init()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.config.value])
    /**
     * récupère l'état (présence d'erreur) du champ
     * @param field nom du champ
     * @param currentValue nouvelle value
     */
    const getFormState = (field: string, currentValue: unknown) => {
        const s = FormFieldService.getInstance().send(
            formLink,
            field,
            currentValue
        )
        if (
            s &&
            (s.error !== errorState.hasError || s.msg !== errorState.helperText)
        ) {
            setError({
                hasError: s.error,
                helperText: s.msg,
            })
        }
    }

    useEffect(() => {
        // si il y a une erreur remonté par le form alors on fait une interogation pour avoir le nouveau formState
        if (error && fieldName)
            getFormState(
                fieldName,
                StoreService.getInstance().local.get(fieldName)
            )

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    /**
     * Affiche / cache le mot de passe
     */
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    }

    /**
     * Empeche la dispersion de l'event mouseDown
     * @param event
     */
    const handleMouseDownPassword = (event: any) => {
        event?.preventDefault()
    }
    /**
     * Callback de gestion des changements de la value
     * @param event
     */
    const onChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        event.preventDefault()
        if (typeof max === "number" && event.target.value.length >= max) {
            return
        }

        // check spécial sur type currency
        if (fieldType === "currency") {
            if (Utils.decimalCheck(event.target.value))
                setValue(event.target.value)
        } else setValue(event.target.value)
        const newValue =
            fieldType === "currency"
                ? Utils.currencyToDatabaseFormat(event.target.value)
                : event.target.value
        // transmission de l'event au composant pere (CwForm)
        if (fieldName) {
            StoreService.getInstance().local.set(fieldName, newValue)
            getFormState(fieldName, newValue)
        }
        if (config.onChange) config.onChange(newValue)
        if (onChangeActions) ActionService.exec(onChangeActions)
    }
    /**
     * Callback de gestion des changements de la value pour champ phone
     * @param event
     */
    const handleChangeInputPhone = (
        inputValue: string,
        data: CountryData,
        event: React.ChangeEvent<HTMLInputElement> | null,
        formattedValue: string
    ) => {
        const dial = (data as any)?.dialCode || ""
        // If country changed (event is null), seed with the country's dial code
        if (!event && dial) {
            setValue(dial)
            if (fieldName) {
                const prefixed = `+${dial}`
                StoreService.getInstance().local.set(fieldName, prefixed)
                getFormState(fieldName, prefixed)
            }
            if (config.onChange) config.onChange(`+${dial}`)
            if (onChangeActions) ActionService.exec(onChangeActions)
            return
        }

        let trimmedVal = inputValue
        if (
            data.countryCode === "fr" &&
            trimmedVal.length >= 3 &&
            trimmedVal.charAt(2) === "0"
        ) {
            trimmedVal = trimmedVal.slice(0, 2) + trimmedVal.slice(3)
        }
        trimmedVal = `+${trimmedVal}`
        setValue(inputValue)
        // transmission de l'event au composant pere (CwForm)
        if (fieldName) {
            StoreService.getInstance().local.set(fieldName, trimmedVal)
            getFormState(fieldName, trimmedVal)
        }
        if (config.onChange) config.onChange(trimmedVal)
        if (onChangeActions) ActionService.exec(onChangeActions)
    }

    // résoudre les couleurs
    const color = config.color
        ? UIService.getColorByName(config.color)
        : "#212121"
    const borderColor = config.borderColor
        ? UIService.getColorByName(config.borderColor)
        : "#212121"
    const colorIcon = config.colorIcon
        ? UIService.getColorByName(config.colorIcon)
        : "#212121"
    const renderTextInput = () => {
        if (edit && fieldType === "phone")
            return (
                <Box className="fullWidth fullHeight mb-1 mr-1">
                    <Typography
                        variant="caption"
                        color={errorState.hasError ? "red" : ""}
                    >
                        {label} {required ? "*" : ""}
                    </Typography>
                    <PhoneInput
                        key={`phone-${phoneCountryFromLang}`}
                        country={phoneCountryFromLang}
                        masks={{
                            fr: ". .. .. .. ...",
                            it: "... .......",
                        }}
                        inputStyle={{
                            borderColor: errorState.hasError
                                ? "red"
                                : borderColor || color,
                            width: "100%",
                            height: "56px",
                        }}
                        value={value}
                        onChange={handleChangeInputPhone}
                        countryCodeEditable={false}
                        placeholder={label}
                        disabled={readOnly}
                        localization={fr}
                        preferredCountries={["fr", "it", "gb", "es"]}
                        defaultErrorMessage={
                            errorState.helperText
                                ? (t(errorState.helperText) as string)
                                : ""
                        }
                    />
                    {errorState.hasError && (
                        <p
                            style={{
                                color: "red",
                                fontSize: "0.75rem",
                                margin: "6px 14px 0 14px",
                            }}
                        >
                            {errorState.helperText
                                ? (t(errorState.helperText) as string)
                                : ""}
                        </p>
                    )}
                </Box>
            )

        if (edit) {
            return (
                <TextField
                    fullWidth
                    name={fieldName}
                    type={showPassword ? "text" : fieldType}
                    label={label}
                    variant={variant}
                    margin={margin}
                    value={value}
                    onChange={onChange}
                    disabled={readOnly}
                    error={errorState.hasError}
                    helperText={
                        errorState.helperText ? t(errorState.helperText) : ""
                    }
                    required={required}
                    multiline={multiline}
                    minRows={minRows}
                    maxRows={maxRows}
                    InputLabelProps={{
                        sx: {
                            color,
                            [`&.${inputLabelClasses.shrink}`]: {
                                color,
                            },
                        },
                    }}
                    sx={{
                        input: {
                            color,
                        },
                        fieldset: {
                            borderColor: borderColor || color,
                        },
                        margin: "5px 0 5px 0",
                    }}
                    InputProps={{
                        startAdornment: labelIcon ? (
                            <InputAdornment position="start">
                                <Icon>{labelIcon}</Icon>
                            </InputAdornment>
                        ) : undefined,
                        endAdornment:
                            fieldType === "password" ? (
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            ) : undefined,
                        sx: { borderRadius },
                    }}
                    className={textFieldClass}
                />
            )
        }
        return (
            <>
                {labelIcon && edit === undefined ? (
                    <Icon className="labelIcon" style={{ color: colorIcon }}>
                        {labelIcon}
                    </Icon>
                ) : null}
                <Typography
                    className={`labelTypo ${labelClassName} `}
                    variant="caption"
                    style={{ color: colorIcon }}
                >
                    {`${label}: `}
                </Typography>
                <Typography variant={variantTypo} style={{ color }}>
                    {fieldType === "password" ? (
                        <PasswordIcon />
                    ) : (
                        Utils.specialTypeRender(pipe, value, fieldName)
                    )}
                </Typography>
            </>
        )
    }

    return !hidden ? (
        <Box
            className={`typoBox ${className} ${margin === "dense" ? "dense" : "mt-2"
                }`}
            style={{ background }}
        >
            {renderTextInput()}
        </Box>
    ) : null
}
export default CwTextfield
